import React, { useState, useEffect } from 'react';
import { BlockedAttempt, ApiResponse } from '../../types';
import { settingsService, SearchParams } from '../../services/api';
import { formatDateTime } from '../../utils/datetime';
import BlockedAttemptsMap from './BlockedAttemptsMap';

const BlockedAttemptsTable: React.FC = () => {
  const [view, setView] = useState<'list' | 'map'>('list');
  const [entries, setEntries] = useState<BlockedAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    page: 1,
    limit: 25,
    search: '',
    sort_by: 'last_attempted_at',
    sort_order: 'DESC',
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 0,
    total_count: 0,
    limit: 25,
    has_next: false,
    has_prev: false,
  });

  const [busyIp, setBusyIp] = useState<string | null>(null);
  const [doneIps, setDoneIps] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: ApiResponse<BlockedAttempt> = await settingsService.listBlockedAttempts(searchParams);
      setEntries(response.entries || []);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to fetch blocked attempts');
      console.error('Error fetching blocked attempts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const search = new FormData(e.currentTarget).get('search') as string;
    setSearchParams(prev => ({ ...prev, search: search.trim(), page: 1 }));
  };

  const handleSort = (field: string) => {
    setSearchParams(prev => ({
      ...prev,
      sort_by: field,
      sort_order: prev.sort_by === field && prev.sort_order === 'ASC' ? 'DESC' : 'ASC',
      page: 1,
    }));
  };

  const handleAddToAllowlist = async (entry: BlockedAttempt) => {
    setActionError(null);
    setBusyIp(entry.ip);
    try {
      await settingsService.addAllowlistEntry(`${entry.ip}/32`, `Added from blocked attempts on ${new Date().toLocaleDateString()}`);
      setDoneIps(prev => new Set(prev).add(entry.ip));
    } catch (err: any) {
      if (err.response?.status === 409) {
        setDoneIps(prev => new Set(prev).add(entry.ip));
      } else {
        setActionError(err.response?.data?.error || 'Failed to add to allowlist');
      }
    } finally {
      setBusyIp(null);
    }
  };

  // "US" -> 🇺🇸 via regional-indicator codepoints; empty for unknown codes.
  const countryFlag = (cc: string | null): string =>
    cc && /^[A-Za-z]{2}$/.test(cc)
      ? String.fromCodePoint(...Array.from(cc.toUpperCase()).map(c => 0x1f1e6 + c.charCodeAt(0) - 65))
      : '';

  const formatLocation = (entry: BlockedAttempt): string => {
    if (!entry.country) return '—';
    const flag = countryFlag(entry.country_code);
    const parts = [entry.country, entry.region, entry.city].filter(Boolean);
    return `${flag ? flag + ' ' : ''}${parts.join(' · ')}`;
  };

  if (loading) return <div className="loading">Loading blocked attempts...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="settings-panel">
      <div className="blocked-header-row">
        <h3>Blocked SIP/RTP attempts</h3>
        <div className="settings-subnav">
          <button
            type="button"
            className={`nav-button ${view === 'list' ? 'active' : ''}`}
            onClick={() => setView('list')}
          >
            List
          </button>
          <button
            type="button"
            className={`nav-button ${view === 'map' ? 'active' : ''}`}
            onClick={() => setView('map')}
          >
            Map
          </button>
        </div>
      </div>
      <p className="settings-hint">
        Aggregated counters, not a per-packet log — updated on a ~15s delay from the recorder.
      </p>

      {view === 'map' ? (
        <BlockedAttemptsMap />
      ) : (
      <>
      <form onSubmit={handleSearch} className="search-form">
        <input name="search" type="text" placeholder="Search by IP..." defaultValue={searchParams.search} />
        <button type="submit">Search</button>
        <button type="button" onClick={() => setSearchParams(prev => ({ ...prev, search: '', page: 1 }))}>Clear</button>
      </form>

      {actionError && <div className="error">{actionError}</div>}

      <div className="pagination-info">
        Showing {entries.length} of {pagination.total_count} blocked IPs
      </div>

      <div className="table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('ip')} className="sortable">
                IP {searchParams.sort_by === 'ip' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th>Location</th>
              <th onClick={() => handleSort('transport')} className="sortable">
                Transport {searchParams.sort_by === 'transport' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('attempt_count')} className="sortable">
                Attempts {searchParams.sort_by === 'attempt_count' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('first_attempted_at')} className="sortable">
                First seen {searchParams.sort_by === 'first_attempted_at' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('last_attempted_at')} className="sortable">
                Last seen {searchParams.sort_by === 'last_attempted_at' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={7} className="no-data">No blocked attempts recorded</td></tr>
            ) : (
              entries.map(entry => (
                <tr key={`${entry.ip}-${entry.transport}`}>
                  <td className="monospace">{entry.ip}</td>
                  <td className="small" title={entry.org || undefined}>{formatLocation(entry)}</td>
                  <td>{entry.transport}</td>
                  <td>{entry.attempt_count.toLocaleString()}</td>
                  <td>{formatDateTime(entry.first_attempted_at)}</td>
                  <td>{formatDateTime(entry.last_attempted_at)}</td>
                  <td>
                    {entry.allowlisted || doneIps.has(entry.ip) ? (
                      <span
                        className="status status-completed"
                        title="This IP is covered by the SIP/RTP allowlist (see Settings → SIP/RTP Allowlist)"
                      >
                        In allowlist
                      </span>
                    ) : (
                      <button type="button" disabled={busyIp === entry.ip} onClick={() => handleAddToAllowlist(entry)}>
                        {busyIp === entry.ip ? 'Adding…' : 'Add to allowlist'}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total_pages > 1 && (
        <div className="pagination">
          <button disabled={!pagination.has_prev} onClick={() => setSearchParams(prev => ({ ...prev, page: pagination.current_page - 1 }))}>Previous</button>
          <span className="page-info">Page {pagination.current_page} of {pagination.total_pages}</span>
          <button disabled={!pagination.has_next} onClick={() => setSearchParams(prev => ({ ...prev, page: pagination.current_page + 1 }))}>Next</button>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default BlockedAttemptsTable;
